import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { API_URL } from "../constants"

const initialState = {
  users: [],
  user: null,
  associatedUsers: [],
  loading: false,
  error: null,
  success: false,
  activeTab: "active",
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    pageSize: 50,
    hasNextPage: false,
    hasPrevPage: false,
  },
}

// Get all users
export const getUsers = createAsyncThunk("users/getUsers", async (params = {}, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const queryParams = new URLSearchParams()

    // Handle both old format (string status) and new format (object with params)
    if (typeof params === "string") {
      queryParams.append("status", params)
    } else {
      if (params.status) queryParams.append("status", params.status)
      if (params.page) queryParams.append("page", params.page)
      if (params.limit) queryParams.append("limit", params.limit)
      if (params.role) queryParams.append("role", params.role)
      if (params.search) queryParams.append("search", params.search)
    }

    const { data } = await axios.get(`${API_URL}/api/users?${queryParams.toString()}`, config)

    return data
  } catch (error) {
    return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message)
  }
})

// Get user by ID
export const getUserById = createAsyncThunk("users/getUserById", async (id, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.get(`${API_URL}/api/users/${id}`, config)

    return data
  } catch (error) {
    return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message)
  }
})

// Create user
export const createUser = createAsyncThunk("users/createUser", async (userData, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.post(`${API_URL}/api/users`, userData, config)

    return data
  } catch (error) {
    return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message)
  }
})

// Update user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, userData }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(`${API_URL}/api/users/${id}`, userData, config)

      return data
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message ? error.response.data.message : error.message,
      )
    }
  },
)

// Update user status
export const updateUserStatus = createAsyncThunk(
  "users/updateUserStatus",
  async ({ id, status }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(`${API_URL}/api/users/${id}/status`, { status }, config)

      return { id, status, data }
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message ? error.response.data.message : error.message,
      )
    }
  },
)

// Get users by parent ID
export const getUsersByParentId = createAsyncThunk(
  "users/getUsersByParentId",
  async (parentId, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/users/parent/${parentId}`, config)

      return data
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message ? error.response.data.message : error.message,
      )
    }
  },
)

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    resetUserState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all users
      .addCase(getUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.loading = false
        if (Array.isArray(action.payload)) {
          // Old format - just an array of users
          state.users = action.payload
        } else {
          // New format - object with users array and pagination
          state.users = action.payload.users || []
          state.pagination = action.payload.pagination || state.pagination
        }
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get user by ID
      .addCase(getUserById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update user status
      .addCase(updateUserStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get users by parent ID
      .addCase(getUsersByParentId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getUsersByParentId.fulfilled, (state, action) => {
        state.loading = false
        state.associatedUsers = action.payload
      })
      .addCase(getUsersByParentId.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { resetUserState, setActiveTab } = userSlice.actions

export default userSlice.reducer
